import { Box, Tooltip } from "@mui/material"
import type { FC } from "react"

import StyledChip from "@/components/common/Chip/Chip"
import { type Invoice, InvoiceStatus } from "@/graphql/codegen/graphql"
import { InvoiceAs400SyncIcon } from "@/screens/Accounting/subfeatures/Invoices/components/InvoiceAs400SyncIcon"
import {
  AS400_PAYABLE_APPROVAL_SYNC_FAILED_TOOLTIP,
  AS400_SYNC_PENDING_TOOLTIP,
} from "@/screens/Accounting/subfeatures/Invoices/constants/invoiceAs400Sync"
import { INVOICE_STATUS_CHIP_CLASSES } from "@/screens/Accounting/subfeatures/Invoices/constants/labels"
import { InvoiceAs400SyncIndicatorVariant } from "@/screens/Accounting/subfeatures/Invoices/types/invoiceAs400Sync"
import {
  isApproveSyncFailed,
  isApproveSyncInProgress,
} from "@/screens/Accounting/subfeatures/Invoices/utils/invoiceAs400Sync"

interface PayableApprovalStatusCellProps {
  invoice: Invoice
}

// AK-5232 AC2/AC3: an approver whose approval is still syncing with AS400 sees the invoice as Pending Approval with a
// grey sync icon; one whose sync failed sees Needs Approval with a red warning and can approve again.
export const PayableApprovalStatusCell: FC<PayableApprovalStatusCellProps> = ({ invoice }) => {
  const isSyncPending = isApproveSyncInProgress(invoice)
  const isSyncFailed = isApproveSyncFailed(invoice)

  return (
    <Box className='flex items-center gap-1'>
      <StyledChip
        label={isSyncPending ? "Pending Approval" : "Needs Approval"}
        className={INVOICE_STATUS_CHIP_CLASSES[InvoiceStatus.PendingApproval]}
        size='small'
      />
      {isSyncPending && (
        <Tooltip title={AS400_SYNC_PENDING_TOOLTIP}>
          <InvoiceAs400SyncIcon
            variant={InvoiceAs400SyncIndicatorVariant.Pending}
            data-testid={`payable-approvals.status-cell.sync-pending-icon-${invoice.invoiceId}`}
          />
        </Tooltip>
      )}
      {isSyncFailed && (
        <Tooltip title={AS400_PAYABLE_APPROVAL_SYNC_FAILED_TOOLTIP}>
          <InvoiceAs400SyncIcon
            variant={InvoiceAs400SyncIndicatorVariant.Failed}
            data-testid={`payable-approvals.status-cell.sync-failed-icon-${invoice.invoiceId}`}
          />
        </Tooltip>
      )}
    </Box>
  )
}
